import { type BudgetScope, useResolvedBudgetScope } from "./useBudgetScope";
import { useFetchBudgetVersions } from "./useFetchBudgetVersions";

/**
 * The key of the budget list a brand selection reads: null for a
 * tenant-scoped tenant's own list, the brand for a brand-scoped one, and
 * undefined when there is no list to read (the scope is still loading, or a
 * brand-scoped tenant has no brand).
 */
export function budgetListKey(
  scope: BudgetScope | undefined,
  brand: string | undefined
): string | null | undefined {
  if (scope === "tenant") return null;
  return scope === "brand" && brand ? brand : undefined;
}

/**
 * Budget versions for a brand selection, resolved against the tenant's budget
 * scope (see {@link budgetListKey}), with the scope and key the list was read
 * for.
 *
 * Every budget-version surface (page selector, summary source, copy source,
 * create modals) goes through here so the brand requirement is relaxed in one
 * place rather than per call site.
 */
export function useScopedBudgetVersions(
  brand: string | undefined,
  enabled: boolean = true
) {
  // Waits for the scope: a brand-keyed list fetched for a tenant-scoped
  // tenant would show its budgets as gone.
  const scope = useResolvedBudgetScope();
  const key = budgetListKey(scope, brand);
  const query = useFetchBudgetVersions(
    key === undefined ? "" : key,
    enabled && key !== undefined
  );
  // Read only the fields callers use: spreading the query reads every tracked
  // field, so each fetch-state change would re-render every caller.
  return { data: query.data, isLoading: query.isLoading, scope, key };
}
